import React from "react";
import { Link } from "react-router-dom";
import { Users, Megaphone, Vote, MessageSquare, Calendar, Sparkles } from "lucide-react";
import { CefLogoImg } from "@/components/site/BrandLogo";
import PageBlocks from "@/components/site/PageBlocks";
import PageHeader from "@/components/site/PageHeader";
import EditableText from "@/components/site/EditableText";

export default function EspaceCEF() {
  return (
    <div>
      <section className="border-b-2 border-black bg-cef-yellow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20 relative">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="chip bg-black text-white">Espace CEF</span>
              <div className="mt-3">
                <PageHeader slug="cef" fallbackTitle="Le [Comité](pink) des Élèves Francophones" fallbackSubtitle="Le CEF, c'est l'organisation officielle des élèves du secondaire en Fédération Wallonie-Bruxelles. Faite par et pour des élèves, depuis plus de 50 ans." />
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/rejoindre" className="neo-btn bg-cef-pink text-white" data-testid="cef-join-btn"><Users className="w-4 h-4" /> Rejoindre le CEF</Link>
                <a href="#projets" className="neo-btn bg-white" data-testid="cef-projects-btn">Nos projets</a>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="neo-card bg-white p-6 sm:p-10 max-w-md w-full">
                <CefLogoImg className="w-full h-auto" alt="Comité des Élèves Francophones" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter uppercase">Qui on est</h2>
        <p className="mt-4 text-lg leading-relaxed">Le CEF est <strong>l'Organisation Représentative des Élèves</strong> reconnue officiellement par la Fédération Wallonie-Bruxelles. Concrètement : on parle aux Ministres, on rédige des avis, on lance des consultations, on forme des délégués d'écoles, et surtout on porte la voix des élèves là où ça se décide.</p>

        <PageBlocks slug="cef" className="mt-6" />

        <div className="mt-8 grid sm:grid-cols-3 gap-4">
          <div className="neo-card p-5"><MessageSquare className="w-7 h-7" /><div className="mt-2 font-display font-black text-xl">Représenter</div><p className="mt-1 text-sm">Porter ta parole dans les instances officielles.</p></div>
          <div className="neo-card p-5"><Vote className="w-7 h-7" /><div className="mt-2 font-display font-black text-xl">Consulter</div><p className="mt-1 text-sm">Te demander ton avis pour construire des positions.</p></div>
          <div className="neo-card p-5"><Megaphone className="w-7 h-7" /><div className="mt-2 font-display font-black text-xl">Mobiliser</div><p className="mt-1 text-sm">Organiser des actions, des marches, des campagnes.</p></div>
        </div>
      </section>

      <section id="projets" className="border-t-2 border-black bg-cef-paper">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter uppercase">Nos projets phares</h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-5">
            {[
              { t: "#PasSeuleEcole", d: "Campagne contre le harcèlement scolaire. Kit clé en main pour chaque conseil d'élèves.", c: "bg-cef-pink text-white" },
              { t: "École 2030", d: "Grande consultation : à quoi devrait ressembler l'école dans 5 ans ?", c: "bg-cef-blue text-white" },
              { t: "Forum d'orientation par les élèves", d: "Anciens élèves, étudiants, on partage des conseils entre nous.", c: "bg-cef-yellow" },
              { t: "Académie des délégués", d: "Formation gratuite, 2 weekends par an, pour devenir un·e meilleur·e délégué·e.", c: "bg-cef-green" },
            ].map((p) => (
              <div key={p.t} className={`neo-card p-6 ${p.c}`}>
                <div className="font-display font-black text-xl tracking-tight">{p.t}</div>
                <p className="mt-2 text-sm">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter uppercase">Notre histoire</h2>
        <div className="mt-6 space-y-4">
          {[
            { y: "1975", t: "Naissance du CEF lors d'une grande mobilisation lycéenne." },
            { y: "1997", t: "Reconnaissance officielle comme Organisation Représentative des Élèves." },
            { y: "2015", t: "Participation active au Pacte pour un Enseignement d'excellence." },
            { y: "2024", t: "Lancement de la plateforme Élève ta voix." },
          ].map((e) => (
            <div key={e.y} className="neo-card p-4 flex gap-4 items-start">
              <div className="chip bg-cef-yellow shrink-0">{e.y}</div>
              <div className="text-sm">{e.t}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t-2 border-black bg-black text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 text-center">
          <h2 className="font-display font-black text-3xl sm:text-5xl tracking-tighter uppercase">Le CEF, c'est <span className="text-cef-yellow">toi</span>.</h2>
          <EditableText
            slug="cef"
            sectionKey="bottom-cta"
            className="mt-3 max-w-2xl mx-auto text-white/80 [&_p]:m-0"
          >
            <p>Tu veux rejoindre l'équipe, devenir délégué·e, ou juste te tenir informé·e ?</p>
          </EditableText>
          <Link to="/rejoindre" className="neo-btn bg-cef-yellow text-black mt-6" data-testid="cef-bottom-join"><Calendar className="w-4 h-4" /> Rejoindre le CEF</Link>
        </div>
      </section>
    </div>
  );
}
